/**
 * Automated QC (1.0.0-rc.8) in the asset view and the Library: the QC badge, "Run QC…" (once per version, for people
 * with asset.qc), and Run QC on several assets. Fixes after rc.9 testing: a run whose every finding was accepted shows
 * as passed ("findings accepted"), and administrators (qc.rerun) can Run QC again on a version already checked.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Check, Loader2, RotateCcw, ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react';
import { BULK_ACTION_LIMIT, qcStatusLabel, type MasterChoice, type MasterChoiceOrAll, type MasterDTO, type QcResolution, type QcRunStatus } from '@prism/contracts';
import { MasterPicker } from './MasterPicker';
import { api, errorMessage } from '../api/client';
import { useAssetQc, useMe, useQcProfiles } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { dateTime } from '../lib/format';
import { Modal } from './Modal';
import { toast } from './Toasts';

const BADGE: Record<QcRunStatus, { cls: string; icon: React.ReactNode; label: string }> = {
  passed: { cls: 'qc-passed', icon: <ShieldCheck size={11} />, label: 'QC passed' },
  warnings: { cls: 'qc-warnings', icon: <ShieldAlert size={11} />, label: 'QC warnings' },
  failed: { cls: 'qc-failed', icon: <ShieldX size={11} />, label: 'QC failed' },
  running: { cls: 'qc-running', icon: <Loader2 size={11} className="spin" />, label: 'QC running' },
  queued: { cls: 'qc-running', icon: <Loader2 size={11} className="spin" />, label: 'QC queued' },
  error: { cls: 'qc-error', icon: <ShieldAlert size={11} />, label: 'QC error' },
};

/** "All findings accepted, the last by … on …". */
export const resolvedTitle = (r: QcResolution) => `All findings accepted; the last by ${r.by ?? 'someone'}, ${dateTime(r.at)}`;

/**
 * The asset's QC: passed, warnings, failed, or running. Clicking opens the QC tab. `status` is what the run counts as
 * (effectiveStatus); with `resolved` (every finding accepted) it reads "QC passed · findings accepted".
 */
export function QcBadge({ status, resolved, onClick, compact = false }: { status: QcRunStatus; resolved?: QcResolution | null; onClick?: () => void; compact?: boolean }) {
  const b = resolved ? BADGE.passed : BADGE[status];
  const label = resolved ? 'QC passed · findings accepted' : b.label;
  const mark = resolved ? <span className="qc-accepted-mark" data-testid="qc-accepted-mark"><Check size={9} strokeWidth={3} />{!compact && 'accepted'}</span> : null;
  const inner = <>{b.icon}{!compact && <span style={{ marginLeft: 4 }}>{resolved ? 'QC passed' : b.label}</span>}{mark}</>;
  const title = resolved ? `${label}. ${resolvedTitle(resolved)}` : label;
  const shown = resolved ? 'passed' : status;
  return onClick
    ? <button type="button" className={`pill qc-badge ${b.cls} ${compact ? 'compact' : ''}`} onClick={onClick} title={`${title}: open the QC tab`} data-testid="qc-badge" data-status={shown} data-resolved={resolved ? 'true' : undefined}>{inner}</button>
    : <span className={`pill qc-badge ${b.cls} ${compact ? 'compact' : ''}`} title={title} data-testid="qc-badge" data-status={shown} data-resolved={resolved ? 'true' : undefined}>{inner}</span>;
}

/**
 * "Run QC…" on the asset page. Hidden without asset.qc. Greyed out once QC was performed on the current version (its
 * tooltip says when and with what result, and links to the QC tab), and while a run is queued or running. A run that
 * ended in error, and a new version, make it available again. Administrators (qc.rerun) get Run QC again in the tooltip.
 */
export function RunQcButton({ assetId, title, onOpenTab }: { assetId: string; title: string; onOpenTab: () => void }) {
  const me = useMe().data!;
  const q = useAssetQc(me.permissions.includes('asset.qc') ? assetId : undefined);
  const [open, setOpen] = useState<false | 'run' | 'rerun'>(false);
  if (!me.permissions.includes('asset.qc')) return null;
  const a = q.data?.availability;
  const done = a?.reason === 'done' && a.run;
  const running = a?.reason === 'running';
  const blocked = !a || !a.canRun;
  const rerun = Boolean(done && q.data?.canRerun);
  const why = done ? `QC already performed on this version (${qcStatusLabel(a.run!.effectiveStatus, a.run!.resolved).toLowerCase()}, ${dateTime(a.run!.finishedAt)})`
    : running ? 'QC running' : a?.reason === 'not_media' ? 'A graphic has no picture or sound to check' : a?.reason === 'no_media' ? 'No media yet' : undefined;
  return (
    <span className="qc-run-wrap" data-testid="run-qc-wrap">
      <button className="btn sm" disabled={blocked} onClick={() => setOpen('run')} data-testid="run-qc" aria-describedby={why ? `run-qc-why-${assetId}` : undefined}>
        {running ? <Loader2 size={14} className="spin" /> : <BadgeCheck size={14} />} <span className="btn-label">{running ? 'QC running' : 'Run QC…'}</span>
      </button>
      {why && (
        <span className="qc-run-tip" role="tooltip" id={`run-qc-why-${assetId}`} data-testid="run-qc-tip">
          {why}. {(done || running) && <button type="button" className="link-button" onClick={onOpenTab} data-testid="run-qc-tip-link">Open the QC tab</button>}
          {rerun && <>{done ? '.' : ''} Administrators can run it again: <button type="button" className="link-button" onClick={() => setOpen('rerun')} data-testid="run-qc-tip-rerun">Run QC again…</button></>}
        </span>
      )}
      {open && <RunQcDialog assetIds={[assetId]} name={title} rerun={open === 'rerun' ? { profileId: a?.run?.profileId ?? null } : undefined} onClose={() => setOpen(false)} />}
    </span>
  );
}

/**
 * Choose a profile (the default preselected) and start QC on one asset or several. `rerun` (qc.rerun): check a version
 * already checked again, after a confirmation, with the last run's profile preselected.
 */
export function RunQcDialog({ assetIds, name, rerun, skippedBefore = 0, onClose, onDone, masters, master: initialMaster = null, masterOnly = false }: {
  assetIds: string[]; name: string; rerun?: { profileId: string | null };
  /** Assets of the list left out before the dialog (no media to check), shown with the count that will start. */
  skippedBefore?: number; onClose: () => void; onDone?: () => void;
  /** 1.2.0: one asset's masters, to choose which is checked (default: the primary). Several assets choose a variant. */
  masters?: MasterDTO[];
  /** 1.2.0: the master chosen when the dialog opens (null: the primary). */
  master?: MasterChoice | null;
  /**
   * 1.2.0: the QC tab's "Run QC on a master…": a further master is preselected, and the once-per-version rule of the
   * primary does not apply (a master's run doesn't change the asset's QC status).
   */
  masterOnly?: boolean;
}) {
  const profiles = useQcProfiles();
  const me = useMe().data;
  const qc = useQueryClient();
  const firstFurther = masters?.find((m) => !m.primary);
  const [masterValue, setMaster] = useState<MasterChoiceOrAll | null>(initialMaster ?? (masterOnly && firstFurther ? { type: 'master', masterId: firstFurther.id } : null));
  const primary = masters?.find((m) => m.primary);
  // Choosing the primary itself is the default (no master in the body).
  const master = masterValue && masterValue.type !== 'all' && !(masterValue.type === 'master' && masterValue.masterId === primary?.id) ? masterValue : null;
  const showPicker = assetIds.length > 1 || (masters?.length ?? 0) > 1;
  const [profileId, setProfileId] = useState<string>('');
  const [includeChecked, setIncludeChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const last = rerun?.profileId && profiles.data?.items.some((p) => p.id === rerun.profileId) ? rerun.profileId : '';
  const chosen = profileId || last || profiles.data?.defaultProfileId || '';
  const many = assetIds.length > 1;
  const mayRerun = Boolean(me?.permissions.includes('qc.rerun'));
  async function start() {
    setBusy(true);
    try {
      if (many) {
        const r = await api<{ started: number; alreadyChecked: number; running: number; skipped: number; checkedAgain: number; overLimit?: number }>('/api/v1/qc/bulk', {
          method: 'POST', json: { assetIds, profileId: chosen || null, ...(includeChecked ? { includeChecked: true } : {}), ...(master ? { master } : {}) },
        });
        toast(`QC: ${includeChecked ? '' : `${r.alreadyChecked} already checked, `}${r.running} running, ${r.started} started${r.checkedAgain ? ` (${r.checkedAgain} checked again)` : ''}${r.skipped ? `, ${r.skipped} skipped (no media)` : ''}${r.overLimit ? `, ${r.overLimit} not started (you have ${BULK_ACTION_LIMIT} QC runs in progress)` : ''}`);
      } else {
        await api(`/api/v1/assets/${assetIds[0]}/qc`, { method: 'POST', json: { profileId: chosen || null, ...(rerun ? { rerun: true } : {}), ...(master ? { master } : {}) } });
        const label = master?.type === 'master' ? masters?.find((m) => m.id === master.masterId)?.label : null;
        toast(rerun ? `QC started again on ${name}` : `QC started on ${name}${label ? ` (${label})` : ''}`);
      }
      await qc.invalidateQueries({ queryKey: ['qc'] });
      await qc.invalidateQueries({ queryKey: queryKeys.assets() });
      onDone?.();
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
      await qc.invalidateQueries({ queryKey: ['qc'] });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={many ? `Run QC on ${assetIds.length} assets` : rerun ? `Run QC again on ${name}` : `Run QC on ${name}`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !chosen} onClick={() => void start()} data-testid="start-qc">
        {busy ? <Loader2 size={14} className="spin" /> : rerun ? <RotateCcw size={14} /> : <BadgeCheck size={14} />} {rerun ? 'Run QC again' : 'Run QC'}
      </button>
    </>}>
      {rerun && (
        <p data-testid="qc-rerun-confirm">This version was already checked. Run QC on it again? The new run becomes the current one: its findings replace the QC timecode layer, and the earlier runs stay in the run history.</p>
      )}
      <div className="field"><label htmlFor="qc-profile">Profile</label>
        <select id="qc-profile" className="select" value={chosen} onChange={(e) => setProfileId(e.target.value)} data-testid="qc-profile">
          {profiles.data?.items.map((p) => <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (default)' : ''}{p.id === last ? ' (last run)' : ''}</option>)}
        </select>
        <span className="help">{profiles.data?.items.find((p) => p.id === chosen)?.description}</span>
      </div>
      {showPicker && (
        <div className="field" data-testid="qc-master"><label htmlFor="qc-master">Master</label>
          <MasterPicker id="qc-master" masters={assetIds.length > 1 ? undefined : masters} value={masterValue} onChange={setMaster}
            defaultLabel={primary ? `Primary master: ${primary.label}` : 'Primary master'} testId="qc-master-picker" />
          <span className="help">The asset’s QC status follows its primary master; a run on another master is listed on the QC tab with that master.</span>
        </div>
      )}
      {many && mayRerun && (
        <label className="toggle-row" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={includeChecked} onChange={(e) => setIncludeChecked(e.target.checked)} data-testid="qc-include-checked" />
          <span>Include already checked (admins)<span className="help" style={{ display: 'block' }}>Check the assets already checked on their current version again; those being checked are still skipped.</span></span>
        </label>
      )}
      {many && (
        <p data-testid="qc-bulk-count">
          QC starts on up to {assetIds.length} asset{assetIds.length === 1 ? '' : 's'}{skippedBefore ? `; ${skippedBefore} in the list have no media to check and are skipped` : ''}.
          At most {BULK_ACTION_LIMIT} at a time, and {BULK_ACTION_LIMIT} of your QC runs in progress at once.
        </p>
      )}
      {!rerun && (
        <p className="help">
          QC runs once per version: after it has passed, warned or failed, a new version is needed to check again{mayRerun ? ' (administrators can run it again)' : ''}. {many && !includeChecked ? 'Assets already checked or being checked are skipped.' : ''}{' '}
          <Link to="/help/quality-control#manual" onClick={onClose}>About Run QC</Link>
        </p>
      )}
    </Modal>
  );
}
